import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { DATEFORMAT_LOCAL_STATE, DATEFORMAT_SHORT_ISO } from './date';

export function convertDateToDayJs(
  date: string | dayjs.Dayjs,
  dateFormat = DATEFORMAT_LOCAL_STATE,
): dayjs.Dayjs {
  dayjs.extend(customParseFormat);
  return typeof date === 'string' ? dayjs(date, dateFormat) : date;
}

// Konvertiert punkteseparierte Datumsangaben in Datumsangaben mit Bindestrichen und umgekehrt
// z.B. 20.12.2022 wird zu 2022-12-20 und 2022-12-20 wird zu 20.12.2022
export function convertDate(date: string): string {
  if (date?.length === 10 && date?.includes('.')) {
    return convertDateFormatFromLocalStateToShortIso(date);
  } else if (date?.length === 10 && date?.includes('-')) {
    return convertDateFormatFromShortIsoToLocalState(date);
  }
  return null;
}

// InputParameter geburtsdatum und stichtag sind im deutschen Datumsformat zu übergeben
// z.B. 31.12.2000. Dieses Format haben wir überlicherweise im Modell gespeichert
export function isYoungerThan(
  geburtsdatum: string | dayjs.Dayjs,
  stichtag: string | dayjs.Dayjs,
  alter: number,
): boolean {
  return convertDateToDayJs(stichtag).diff(convertDateToDayJs(geburtsdatum), 'years') < alter;
}

// InputParameter geburtsdatum und stichtag sind im deutschen Datumsformat zu übergeben
// z.B. 31.12.2000. Dieses Format haben wir üblicherweise im Modell gespeichert
export function isOlderThan(
  geburtsdatum: string | dayjs.Dayjs,
  stichtag: string | dayjs.Dayjs,
  alter: number,
): boolean {
  return convertDateToDayJs(stichtag).diff(convertDateToDayJs(geburtsdatum), 'years') > alter;
}

export function dateAtTheEndOfDay(date: string | dayjs.Dayjs): dayjs.Dayjs {
  return convertDateToDayJs(date, DATEFORMAT_SHORT_ISO)?.endOf('day');
}

export function dateAtTheStartOfDay(date: string | dayjs.Dayjs): dayjs.Dayjs {
  return convertDateToDayJs(date, DATEFORMAT_SHORT_ISO)?.startOf('day');
}

export function convertDateFormatFromShortIsoToLocalState(date: string | dayjs.Dayjs): string {
  return dayjs(date, DATEFORMAT_SHORT_ISO).format(DATEFORMAT_LOCAL_STATE);
}

export function convertDateFormatFromLocalStateToShortIso(date: string | dayjs.Dayjs): string {
  return dayjs(date, DATEFORMAT_LOCAL_STATE).format(DATEFORMAT_SHORT_ISO);
}

export function isChristmasTime(date: string | dayjs.Dayjs = dayjs()): boolean {
  const today = convertDateToDayJs(date);
  const startDate = dayjs(`${today.year()}-12-01`).startOf('day');
  const endDate = dayjs(`${today.year()}-12-31`).endOf('day');
  return today >= startDate && today <= endDate;
}

export function isEasterTime(date: string | dayjs.Dayjs = dayjs()): boolean {
  const today = convertDateToDayJs(date);
  const easter = getEaster(today.year());
  const startDate = dayjs(`${easter.year}-${easter.month}-${easter.day}`)
    .subtract(7, 'days')
    .startOf('day');
  const endDate = dayjs(`${easter.year}-${easter.month}-${easter.day}`).add(3, 'days').endOf('day');
  return today >= startDate && today <= endDate;
}

// modified version of https://gist.github.com/johndyer/0dffbdd98c2046f41180c051f378f343
/* istanbul ignore next */
function getEaster(year: number): { day: string; month: string; year: string } {
  const f = Math.floor,
    // Golden Number - 1
    G = year % 19,
    C = f(year / 100),
    // related to Epact
    H = (C - f(C / 4) - f((8 * C + 13) / 25) + 19 * G + 15) % 30,
    // number of days from 21 March to the Paschal full moon
    I = H - f(H / 28) * (1 - f(29 / (H + 1)) * f((21 - G) / 11)),
    // weekday for the Paschal full moon
    J = (year + f(year / 4) + I + 2 - C + f(C / 4)) % 7,
    // number of days from 21 March to the Sunday on or before the Paschal full moon
    L = I - J,
    month = 3 + f((L + 40) / 44),
    day = L + 28 - 31 * f(month / 4);

  return { year: year.toString(), month: month.toString(), day: day.toString().padStart(2, '0') };
}
